import type { FC } from 'react'

import { Button, ButtonType, Drawer, Tab, TabPane } from '@sok/design-system'
import { StyledSyncLoader } from 'components/common/StyledSyncLoader'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import { useActiveFilters } from 'services/Search/hooks/use-active-filters'
import { type Facet, facetKeyToFilter, isStringFacet } from 'services/Search/utils/filters'
import { useIsClientSide } from 'utils/hooks/use-is-client-side'
import { useURLSearchParams } from 'utils/hooks/use-url-search-params'

import { StringFilter } from './StringFilter'
import {
  StyledDrawerTitle,
  StyledFooter,
  StyledLoaderContainer,
  StyledTabContainer,
  StyledTitleContainer,
} from './styled'
import { TabTitle } from './TabTitle'

/** Same width as `OrderContainer` */
const DESKTOP_WIDTH = 408

/**
 * There should be a small gap to see the backdrop
 * before turning full-screen Drawer into sidebar
 */
export const DESKTOP_LAYOUT_MEDIA_QUERY = { minWidth: DESKTOP_WIDTH + 48 }

export const FULLSCREEN_DRAWER = { height: '100%', maxHeight: '100vh', width: '100vw' }
export const SIDEBAR_DRAWER = { width: DESKTOP_WIDTH }
export const DRAWER_BODY_STYLE = { display: 'flex', flexDirection: 'column' as const, padding: 0 }

interface Props {
  className?: string
  facets?: Facet[] | null
  loading: boolean
  onClose: () => void
  open: boolean
}

export const FilterDrawer: FC<Props> = ({ open, onClose, loading, facets }) => {
  const { t } = useTranslation()
  const isDesktopLayout = useMediaQuery(DESKTOP_LAYOUT_MEDIA_QUERY)
  /** These are kept in local state so that loading doesn't clear them */
  const [visibleFacets, setVisibleFacets] = useState(facets)
  const isClientSide = useIsClientSide()

  useEffect(() => {
    if (!loading && facets) {
      setVisibleFacets(facets)
    }
  }, [loading, facets])

  const { removeParams } = useURLSearchParams()

  const activeFilters = useActiveFilters()

  const activeFilterTotalCount = useMemo(
    () => activeFilters.flatMap(({ stringValue }) => stringValue).length,
    [activeFilters],
  )

  const clearAllFilters = useCallback(() => {
    const filters = activeFilters.map<[string, undefined]>(({ key }) => [
      facetKeyToFilter(key),
      undefined,
    ])
    removeParams(...filters)
  }, [activeFilters, removeParams])

  return isClientSide ? (
    <Drawer
      contentWrapperStyle={isDesktopLayout ? SIDEBAR_DRAWER : FULLSCREEN_DRAWER}
      bodyStyle={DRAWER_BODY_STYLE}
      data-test-id="search-filter-drawer"
      title={
        <StyledTitleContainer>
          {loading && (
            <StyledLoaderContainer aria-label={t`Searching...`} aria-live="polite">
              <StyledSyncLoader />
            </StyledLoaderContainer>
          )}
          <StyledDrawerTitle>{t`Filter`}</StyledDrawerTitle>
        </StyledTitleContainer>
      }
      visible={open}
      placement={isDesktopLayout ? 'right' : 'bottom'}
      onClose={onClose}
    >
      <StyledTabContainer>
        <Tab>
          {visibleFacets
            ? visibleFacets.filter(isStringFacet).map((filter) => (
                <TabPane key={filter.key} tab={<TabTitle facet={filter} />}>
                  <StringFilter facet={filter} />
                </TabPane>
              ))
            : null}
        </Tab>
      </StyledTabContainer>

      <StyledFooter>
        <Button
          data-test-id="search-filter-drawer-clear-filters"
          disabled={activeFilterTotalCount === 0}
          onClick={clearAllFilters}
          text={t`Clear filters`}
          type={ButtonType.TEXT}
        />
        <Button
          data-test-id="search-filter-drawer-show-results"
          disabled={loading}
          onClick={onClose}
          text={t`Show results`}
        />
      </StyledFooter>
    </Drawer>
  ) : null
}
